import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Chat.css';

const API_URL = process.env.REACT_APP_WHATSAPP_API || 'https://whatsapp.socialpartners.in/api';

/* ─── Utilities ──────────────────────────────────────────────────────────── */

const formatTime = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(parseInt(timestamp) * 1000);
  return isNaN(date) ? '' : date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatDate = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(parseInt(timestamp) * 1000);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
};

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2);
};

const getAvatarColor = (name) => {
  const colors = ['#25d366', '#075e54', '#128c7e', '#25aae1', '#f39c12', '#e67e22', '#e74c3c', '#9b59b6'];
  let hash = 0;
  for (let i = 0; i < (name || 'A').length; i++) {
    hash = ((hash << 5) - hash) + (name || 'A').charCodeAt(i);
    hash = hash & hash;
  }
  return colors[Math.abs(hash) % colors.length];
};

/* ─── Sub-components ─────────────────────────────────────────────────────── */

const MessageStatus = ({ status }) => {
  const map = {
    sending:   { icon: '⏱', color: '#9ca3af' },
    sent:      { icon: '✓',  color: '#9ca3af' },
    delivered: { icon: '✓✓', color: '#9ca3af' },
    read:      { icon: '✓✓', color: '#0ea5e9' },
    failed:    { icon: '✗',  color: '#ef4444' },
  };
  const s = map[status] || map.sent;
  return <span style={{ fontSize: 11, color: s.color, marginLeft: 3 }}>{s.icon}</span>;
};

const Avatar = ({ name, size = 40 }) => (
  <div style={{
    width: size, height: size, borderRadius: '50%',
    background: getAvatarColor(name), color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 700, fontSize: size * 0.38, flexShrink: 0,
  }}>
    {getInitials(name)}
  </div>
);

/* ─── Template message card ──────────────────────────────────────────────── */

const TemplateMessage = ({ message, isOwn }) => {
  const { renderedHeader, renderedBody, renderedFooter, renderedButtons, template, body } = message;
  const templateName = template?.name || (body?.match(/^Template:\s*(.+?)\s*\|/) || [])[1] || 'Template';

  const cardBg     = isOwn ? '#d0f0d0' : '#fff';
  const cardBorder = isOwn ? '#b0e0b0' : '#e0e0e0';
  const badgeBg    = isOwn ? '#c0e8c8' : '#f5f5f5';

  return (
    <div style={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 8, overflow: 'hidden', minWidth: 220, maxWidth: '100%' }}>

      {/* badge */}
      <div style={{ padding: '5px 10px', background: badgeBg, borderBottom: `1px solid ${cardBorder}`, display: 'flex', alignItems: 'center', gap: 5 }}>
        <i className="fas fa-layer-group" style={{ fontSize: 10, color: '#f59e0b' }}></i>
        <span style={{ fontSize: 11, color: '#888', fontWeight: 500 }}>{templateName}</span>
      </div>

      {/* header */}
      {renderedHeader && (
        <>
          {renderedHeader.type === 'text' && (
            <div style={{ padding: '10px 12px 2px', fontWeight: 700, fontSize: 14, lineHeight: 1.4 }}>
              {renderedHeader.text}
            </div>
          )}
          {renderedHeader.type === 'image' && (
            <div style={{ background: '#ddd', minHeight: 130, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
              {renderedHeader.url
                ? <img src={renderedHeader.url} alt="header" style={{ width: '100%', objectFit: 'cover' }} />
                : <i className="fas fa-image" style={{ fontSize: 36, color: '#aaa' }}></i>
              }
            </div>
          )}
          {renderedHeader.type === 'video' && (
            <div style={{ background: '#1a1a2e', height: 110, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="fas fa-play-circle" style={{ fontSize: 40, color: 'rgba(255,255,255,0.8)' }}></i>
            </div>
          )}
          {renderedHeader.type === 'document' && (
            <div style={{ padding: '10px 12px', background: '#f0f0f0', borderBottom: `1px solid ${cardBorder}`, display: 'flex', alignItems: 'center', gap: 8 }}>
              <i className="fas fa-file-alt" style={{ fontSize: 22, color: '#e74c3c' }}></i>
              <span style={{ fontSize: 13, color: '#555' }}>Document</span>
            </div>
          )}
        </>
      )}

      {/* body */}
      {renderedBody ? (
        <div style={{ padding: '8px 12px', fontSize: 14, lineHeight: 1.55, whiteSpace: 'pre-wrap', color: '#111' }}>
          {renderedBody}
        </div>
      ) : (
        <div style={{ padding: '8px 12px', fontSize: 13, color: '#999', fontStyle: 'italic' }}>
          Template message
        </div>
      )}

      {/* footer */}
      {renderedFooter && (
        <div style={{ padding: '0 12px 8px', fontSize: 12, color: '#aaa' }}>
          {renderedFooter}
        </div>
      )}

      {/* buttons */}
      {renderedButtons?.length > 0 && (
        <div style={{ borderTop: `1px solid ${cardBorder}` }}>
          {renderedButtons.map((btn, i) => {
            const btnStyle = {
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: 6, padding: '9px 12px',
              color: '#0ea5e9', fontSize: 13, fontWeight: 500,
              borderTop: i > 0 ? `1px solid ${cardBorder}` : 'none',
              textDecoration: 'none', background: 'transparent', width: '100%', border: 'none', cursor: 'pointer',
            };
            if (btn.url) return (
              <a key={i} href={btn.url} target="_blank" rel="noopener noreferrer" style={btnStyle}>
                <i className="fas fa-external-link-alt" style={{ fontSize: 11 }}></i>{btn.text}
              </a>
            );
            if (btn.type === 'PHONE_NUMBER') return (
              <a key={i} href={`tel:${btn.phone_number}`} style={btnStyle}>
                <i className="fas fa-phone" style={{ fontSize: 11 }}></i>{btn.text}
              </a>
            );
            return (
              <div key={i} style={btnStyle}>
                <i className="fas fa-reply" style={{ fontSize: 11 }}></i>{btn.text}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ─── Message bubble ─────────────────────────────────────────────────────── */

const MessageBubble = ({ message, isOwn, senderName }) => {
  const { type, body, status, media_url } = message;
  const isFailed = status === 'failed';
  const bubbleCls = `message-text ${isOwn ? 'message-own' : 'message-received'} ${isFailed ? 'message-failed' : ''}`;

  const renderContent = () => {
    if (type === 'template') return <TemplateMessage message={message} isOwn={isOwn} />;

    if (type === 'image') return (
      <div className="message-image-container">
        {body || media_url
          ? <img src={body || media_url} alt="Image" className="message-image" />
          : <div className={bubbleCls} style={{ color: '#888' }}><i className="fas fa-image me-1"></i> Image</div>
        }
      </div>
    );

    if (type === 'video') return (
      <div className="message-media-container">
        <div className="message-media-icon"><i className="fas fa-video"></i></div>
        <div className="message-media-info">
          <div className="message-media-name">{body || 'Video'}</div>
        </div>
      </div>
    );

    if (type === 'document') return (
      <div className="message-media-container">
        <div className="message-media-icon"><i className="fas fa-file-alt"></i></div>
        <div className="message-media-info">
          <div className="message-media-name">{body || 'Document'}</div>
          {media_url && <div className="message-media-size">{media_url}</div>}
        </div>
      </div>
    );

    if (type === 'audio') return (
      <div className={bubbleCls} style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 140 }}>
        <i className="fas fa-microphone" style={{ color: '#25d366', fontSize: 16 }}></i>
        <span style={{ color: '#888', fontSize: 13 }}>Voice message</span>
      </div>
    );

    if (type === 'sticker') return (
      <div className={bubbleCls} style={{ color: '#888' }}>
        <i className="fas fa-smile me-1"></i> Sticker
      </div>
    );

    if (type === 'reaction') return (
      <div className={bubbleCls} style={{ color: '#888', fontSize: 13 }}>
        {body || '👍 Reaction'}
      </div>
    );

    if (type === 'location') return (
      <div className={bubbleCls} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <i className="fas fa-map-marker-alt" style={{ color: '#e74c3c' }}></i>
        <span>{body || 'Location'}</span>
      </div>
    );

    /* text / button / interactive / unknown */
    return (
      <div className={bubbleCls}>
        {isFailed && <div style={{ fontSize: 11, color: '#ef4444', marginBottom: 3 }}>⚠ Failed to send</div>}
        {body
          ? <span style={{ whiteSpace: 'pre-wrap' }}>{body}</span>
          : <span style={{ color: '#aaa', fontStyle: 'italic', fontSize: 13 }}>Message</span>
        }
      </div>
    );
  };

  return (
    <div className={`message-group ${isOwn ? 'message-group-own' : 'message-group-received'}`}>
      {!isOwn && <Avatar name={senderName || 'Contact'} size={30} />}
      <div className="message-content">
        {renderContent()}
        <div className={`message-time ${isOwn ? 'message-time-own' : ''}`}>
          {formatTime(message.timestamp)}
          {isOwn && <MessageStatus status={status} />}
        </div>
      </div>
    </div>
  );
};

const cleanPreview = (body, type) => {
  if (body) {
    const tpl = body.match(/^Template:\s*(.+?)\s*\|/);
    if (tpl) return `📋 ${tpl[1]}`;
    return body.length > 55 ? body.slice(0, 55) + '…' : body;
  }
  switch (type) {
    case 'image':    return '📷 Photo';
    case 'video':    return '🎥 Video';
    case 'audio':    return '🎙 Voice message';
    case 'document': return '📄 Document';
    case 'sticker':  return '😊 Sticker';
    case 'template': return '📋 Template';
    case 'reaction': return '👍 Reaction';
    case 'location': return '📍 Location';
    default:         return '';
  }
};

const ConversationItem = ({ conversation, isActive, unreadCount, onSelect }) => {
  const name = conversation.profile_name || conversation.wa_id;
  const preview = cleanPreview(conversation.lastMessage, conversation.lastMessageType);
  const lastTime = conversation.lastMessageTime
    ? formatTime(Math.floor(new Date(conversation.lastMessageTime).getTime() / 1000).toString())
    : '';

  return (
    <div
      className={`conversation-item ${isActive ? 'active' : ''} ${unreadCount > 0 ? 'unread' : ''}`}
      onClick={() => onSelect(conversation)}
    >
      <div className="conversation-avatar">
        <Avatar name={name} size={42} />
        {unreadCount > 0 && (
          <div className="unread-badge">{unreadCount > 99 ? '99+' : unreadCount}</div>
        )}
      </div>
      <div className="conversation-content">
        <div className="conversation-header">
          <div className={`conversation-name ${unreadCount > 0 ? 'unread' : ''}`}>{name}</div>
          <div className={`conversation-time ${unreadCount > 0 ? 'unread' : ''}`}>{lastTime}</div>
        </div>
        <div className={`conversation-message ${unreadCount > 0 ? 'unread' : ''}`}
          style={{ color: preview ? undefined : '#bbb', fontStyle: preview ? 'normal' : 'italic' }}>
          {preview || 'No messages'}
        </div>
      </div>
    </div>
  );
};

/* ─── Main Component ─────────────────────────────────────────────────────── */

export default function Chat() {
  const [conversations, setConversations]         = useState([]);
  const [messages, setMessages]                   = useState([]);
  const [selectedContact, setSelectedContact]     = useState(null);
  const [searchTerm, setSearchTerm]               = useState('');
  const [inputMessage, setInputMessage]           = useState('');
  const [loading, setLoading]                     = useState(false);
  const [conversationsLoading, setConversationsLoading] = useState(true);
  const [sending, setSending]                     = useState(false);
  const [error, setError]                         = useState(null);
  const [unreadMap, setUnreadMap]                 = useState({});
  const [showEmojiPicker, setShowEmojiPicker]     = useState(false);
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);

  const chatEndRef  = useRef(null);
  const pollRef     = useRef(null);
  const convPollRef = useRef(null);

  /* ── Fetch conversations ── */
  const fetchConversations = useCallback(async (silent = false) => {
    try {
      if (!silent) setConversationsLoading(true);
      const res  = await fetch(`${API_URL}/conversations`);
      const data = await res.json();
      let list   = Array.isArray(data) ? data : data.data || data.conversations || [];
      list = list.sort(
        (a, b) => new Date(b.lastMessageTime || 0) - new Date(a.lastMessageTime || 0)
      );
      setConversations(list);
      if (!selectedContact && list.length > 0) setSelectedContact(list[0]);
    } catch (err) {
      if (!silent) setError(`Failed to load conversations: ${err.message}`);
    } finally {
      if (!silent) setConversationsLoading(false);
    }
  }, [selectedContact]);

  /* ── Fetch messages ── */
  const fetchMessages = useCallback(async (waId, silent = false) => {
    try {
      if (!silent) setLoading(true);
      const res  = await fetch(`${API_URL}/chat/${waId}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const list = Array.isArray(data) ? data : data.data || [];
      setMessages([...list].sort((a, b) => parseInt(a.timestamp) - parseInt(b.timestamp)));
      setError(null);
    } catch (err) {
      if (!silent) setError(`Failed to load messages: ${err.message}`);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  /* ── Send message ── */
  const handleSend = useCallback(async (e) => {
    e?.preventDefault();
    if (!inputMessage.trim() || !selectedContact || sending) return;
    const body = inputMessage.trim();
    setSending(true);
    setInputMessage('');
    const temp = {
      id: `temp_${Date.now()}`, body,
      direction: 'outbound', type: 'text', status: 'sending',
      timestamp: Math.floor(Date.now() / 1000).toString(),
    };
    setMessages((prev) => [...prev, temp]);
    try {
      const res = await fetch(`${API_URL}/send-message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: selectedContact.wa_id, body, conversationId: selectedContact.conversationId }),
      });
      if (!res.ok) throw new Error('Send failed');
      setTimeout(() => fetchMessages(selectedContact.wa_id, true), 1000);
    } catch {
      setMessages((prev) => prev.map((m) => m.id === temp.id ? { ...m, status: 'failed' } : m));
    } finally {
      setSending(false);
    }
  }, [inputMessage, selectedContact, sending, fetchMessages]);

  /* ── Effects ── */
  useEffect(() => {
    fetchConversations();
    convPollRef.current = setInterval(() => fetchConversations(true), 15000);
    return () => clearInterval(convPollRef.current);
  }, [fetchConversations]);

  useEffect(() => {
    if (!selectedContact) return;
    fetchMessages(selectedContact.wa_id, false);
    pollRef.current = setInterval(() => fetchMessages(selectedContact.wa_id, true), 8000);
    setUnreadMap((prev) => ({ ...prev, [selectedContact.conversationId]: 0 }));
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [selectedContact?.wa_id, fetchMessages]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const filtered     = conversations.filter((c) =>
    (c.profile_name || c.wa_id).toLowerCase().includes(searchTerm.toLowerCase())
  );
  const totalUnread  = Object.values(unreadMap).reduce((a, b) => a + b, 0);
  const contactName  = selectedContact ? (selectedContact.profile_name || selectedContact.wa_id) : '';

  /* ─────────────────────────────── Render ────────────────────────────────── */
  return (
    <div className="page-wrapper">
      <div className="content container-fluid" style={{ padding: 0 }}>

        {/* ── Page title bar ── */}
        <div style={{
          padding: '12px 20px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderBottom: '1px solid #e5e7eb', background: '#fff',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <i className="fe fe-message-circle" style={{ fontSize: 20, color: '#25d366' }}></i>
            <h5 style={{ margin: 0, fontWeight: 700, color: '#111' }}>WhatsApp Chats</h5>
            {totalUnread > 0 && (
              <span style={{
                background: '#25d366', color: '#fff',
                borderRadius: 12, padding: '2px 10px',
                fontSize: 12, fontWeight: 700,
              }}>
                {totalUnread} new
              </span>
            )}
          </div>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => fetchConversations()}
          >
            <i className="fas fa-sync-alt me-1"></i> Refresh
          </button>
        </div>

        {/* ── Chat window ── */}
        <div style={{
          display: 'flex',
          height: 'calc(100vh - 120px)',
          overflow: 'hidden',
          background: '#f5f5f5',
        }}>

          {/* ── Left: conversation list ── */}
          <div style={{
            width: '30%', maxWidth: 380, minWidth: 240,
            background: '#fff',
            borderRight: '1px solid #e0e0e0',
            display: 'flex', flexDirection: 'column',
            overflow: 'hidden',
          }}>
            {/* search */}
            <div className="chat-search-container">
              <input
                type="text"
                className="chat-search"
                placeholder="Search conversations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <i className="fas fa-search chat-search-icon"></i>
            </div>

            {/* list */}
            <div className="conversations-list">
              {conversationsLoading ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Loading conversations...</p>
                </div>
              ) : filtered.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-inbox"></i>
                  <p>No conversations found</p>
                </div>
              ) : filtered.map((conv) => (
                <ConversationItem
                  key={conv.conversationId}
                  conversation={conv}
                  isActive={selectedContact?.conversationId === conv.conversationId}
                  unreadCount={unreadMap[conv.conversationId] || 0}
                  onSelect={setSelectedContact}
                />
              ))}
            </div>
          </div>

          {/* ── Right: chat area ── */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#fff', overflow: 'hidden' }}>
            {!selectedContact ? (
              <div className="empty-chat">
                <i className="fas fa-comments"></i>
                <p>Select a conversation to start chatting</p>
              </div>
            ) : (
              <>
                {/* chat header */}
                <div className="chat-message-header">
                  <div className="chat-contact-info">
                    <Avatar name={contactName} size={40} />
                    <div className="contact-details">
                      <h3 className="contact-name">{contactName}</h3>
                      <p className="contact-number">{selectedContact.wa_id}</p>
                    </div>
                  </div>
                  <div className="chat-actions">
                    <button
                      className="chat-action-btn"
                      onClick={() => fetchMessages(selectedContact.wa_id, false)}
                      title="Refresh"
                    >
                      <i className="fas fa-redo"></i>
                    </button>
                    <a
                      href={`/wa-contacts?wa_id=${selectedContact.wa_id}`}
                      className="chat-action-btn"
                      title="View contact"
                    >
                      <i className="fe fe-user"></i>
                    </a>
                    <button className="chat-action-btn" title="More options">
                      <i className="fe fe-more-vertical"></i>
                    </button>
                  </div>
                </div>

                {/* messages */}
                <div className="messages-container">
                  {loading ? (
                    <div className="loading-state">
                      <div className="spinner"></div>
                      <p>Loading messages...</p>
                    </div>
                  ) : error ? (
                    <div className="error-state">
                      <i className="fas fa-exclamation-circle"></i>
                      <p>{error}</p>
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="empty-state">
                      <i className="fas fa-comment-dots"></i>
                      <p>No messages yet</p>
                    </div>
                  ) : (
                    <div className="messages-list">
                      {messages.map((msg, idx) => {
                        const prev     = idx > 0 ? messages[idx - 1] : null;
                        const showDate = !prev || formatDate(msg.timestamp) !== formatDate(prev.timestamp);
                        return (
                          <React.Fragment key={msg.id || idx}>
                            {showDate && (
                              <div className="message-date-divider">
                                <span>{formatDate(msg.timestamp)}</span>
                              </div>
                            )}
                            <MessageBubble
                              message={msg}
                              isOwn={msg.direction === 'outbound'}
                              senderName={contactName}
                            />
                          </React.Fragment>
                        );
                      })}
                      <div ref={chatEndRef}></div>
                    </div>
                  )}
                </div>

                {/* input area */}
                <form className="chat-input-area" onSubmit={handleSend}>
                  {showAttachmentMenu && (
                    <div className="attachment-menu">
                      {[
                        { type: 'image',    icon: 'fa-image',       label: 'Image' },
                        { type: 'document', icon: 'fa-file',        label: 'Document' },
                        { type: 'video',    icon: 'fa-video',       label: 'Video' },
                        { type: 'audio',    icon: 'fa-microphone',  label: 'Audio' },
                      ].map(({ type, icon, label }) => (
                        <button
                          key={type}
                          type="button"
                          className="attachment-item"
                          onClick={() => setShowAttachmentMenu(false)}
                        >
                          <i className={`fas ${icon}`}></i> {label}
                        </button>
                      ))}
                    </div>
                  )}

                  {showEmojiPicker && (
                    <div className="emoji-picker">
                      {['😀','😂','😍','🎉','👍','❤️','🔥','👏','🚀','💯'].map((emoji) => (
                        <button
                          key={emoji}
                          type="button"
                          className="emoji-item"
                          onClick={() => { setInputMessage((p) => p + emoji); setShowEmojiPicker(false); }}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="input-group">
                    <button
                      type="button"
                      className="input-icon-btn"
                      onClick={() => { setShowAttachmentMenu(!showAttachmentMenu); setShowEmojiPicker(false); }}
                    >
                      <i className="fas fa-paperclip"></i>
                    </button>

                    <input
                      type="text"
                      className="chat-input"
                      placeholder="Type a message…"
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSend();
                        }
                      }}
                      disabled={sending}
                    />

                    <button
                      type="button"
                      className="input-icon-btn"
                      onClick={() => { setShowEmojiPicker(!showEmojiPicker); setShowAttachmentMenu(false); }}
                    >
                      <i className="fas fa-smile"></i>
                    </button>

                    <button
                      type="submit"
                      className="send-btn"
                      disabled={!inputMessage.trim() || sending}
                    >
                      {sending
                        ? <i className="fas fa-spinner fa-spin"></i>
                        : <i className="fas fa-paper-plane"></i>
                      }
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>

        </div>
        {/* /chat window */}

      </div>
    </div>
  );
}
